import React, { useState, useRef, useMemo, forwardRef, useImperativeHandle } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { lighten, styled } from "@mui/material/styles";
import {
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Divider,
} from "@mui/material";
import {
  FileDownload,
  Image,
  ContentCopy,
  Close as CloseIcon,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import * as htmlToImage from "html-to-image";
import axios from "axios";


const primaryColor = "#005EB8"; // Cognizant's primary blue
const lighterBlue = "#B3D6F5"; // 2 shades lighter
const lightestBlue = "#E6F0FA"; // 3 shades lighter
const lightGrey = "#F5F5F5";
const darkGrey = "#D3D3D3";

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  "& .MuiDataGrid-columnHeaders": {
    maxHeight: "none !important",
  },
  "& .MuiDataGrid-columnHeader": {
    "& .MuiDataGrid-columnHeaderTitleContainer": {
      "& .MuiDataGrid-columnHeaderTitle": {
        fontWeight: "bold !important",
        whiteSpace: "normal", // Enable word wrapping
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontSize: "0.85rem !important",
      },
    },
    backgroundColor: lightestBlue, // Default: lightest blue
    color: "#000 !important",
    fontSize: "0.85rem !important",
    textTransform: "none",
    borderBottom: `2px solid ${primaryColor}`,
    height: "auto !important",
    minHeight: "28px !important",
  },
  "& .MuiDataGrid-columnHeader--filledGroup": {
    height: "28px !important",
    minHeight: "28px !important",
    maxHeight: "28px !important",
  },
  "& .MuiDataGrid-columnHeader--emptyGroup": {
    height: "0px !important",
    minHeight: "0px !important",
    maxHeight: "0px !important",
    padding: "0 !important",
    border: "none !important",
  },
  "& .MuiDataGrid-columnHeaderGroup": {
    "& .MuiDataGrid-columnHeaderTitleContainer": {
      justifyContent: "center",
    },
  },
  "& .pdp-group-header": {
    backgroundColor: lighterBlue + " !important",
    color: "#000 !important",
    fontWeight: "bold !important",
    borderBottom: `1px solid ${primaryColor}`,
    height: "28px !important",
    justifyContent: "center !important",
    "& .MuiDataGrid-columnHeaderTitleContainer": {
      justifyContent: "center !important",
    },
  },
  "& .vcdp-group-header": {
    backgroundColor: lighterBlue + " !important",
    color: "#000 !important",
    fontWeight: "bold !important",
    borderBottom: `1px solid ${primaryColor}`,
    height: "28px !important",
    justifyContent: "center !important",
    "& .MuiDataGrid-columnHeaderTitleContainer": {
      justifyContent: "center !important",
    },
  },
  "& .header-primary": {
    backgroundColor: primaryColor + " !important",
    color: "#fff !important",
  },
  "& .header-lighter": {
    backgroundColor: lighterBlue + " !important",
    color: "#000 !important",
  },
  "& .last-row": {
    fontWeight: "bold",
    backgroundColor: lighten(primaryColor, 0.85),
    color: "#000",
  },
  "& .first-column": {
    fontWeight: "bold",
  },
  "& .last-column": {
    fontWeight: "bold",
    backgroundColor: lighten(primaryColor, 0.85),
  },
  "& .negative-value": {
    fontWeight: "bold",
    backgroundColor: "#c8e6c9", // Light green
    color: "#000",
  },
  "& .positive-value": {
    fontWeight: "bold",
    backgroundColor: "#ffe0b2", // Light orange
    color: "#000",
  },
  "& .high-pdp": {
    fontWeight: "bold",
    backgroundColor: "#bbdefb", // Light blue
    color: "#000",
  },
  "& .MuiDataGrid-root": {
    overflowX: "auto",
  },
  "& .MuiDataGrid-cell": {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    borderBottom: `1px solid ${darkGrey}`,
  },
  "& .MuiDataGrid-columnHeaders": {
    whiteSpace: "nowrap",
  },
}));

const StyledClickableCell = styled("div")(({ theme }) => ({
  cursor: "pointer",
  color: theme.palette.primary.main,
  textDecoration: "underline",
  "&:hover": {
    color: theme.palette.primary.dark,
  },
}));

const DashboardTable = forwardRef(({ reportData, filterValues, hideColumns = [], sortingMode = 'server' }, ref) => {
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [columnGroupingModel, setColumnGroupingModel] = useState([]);
  const [openPopup, setOpenPopup] = useState(false);
  const [popupData, setPopupData] = useState({ rows: [], columns: [] });
  const [popupTitle, setPopupTitle] = useState("");
  const [filterContext, setFilterContext] = useState(null);
  const [sortModel, setSortModel] = useState([]);
  const [grandTotalRow, setGrandTotalRow] = useState(null);
  const tableRef = useRef(null);
  const popupTableRef = useRef(null);

  // Expose export functions to parent via ref
  useImperativeHandle(ref, () => ({
    exportExcel: () => {
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Dashboard Data");
      XLSX.writeFile(workbook, "dashboard_data.xlsx");
    },
    exportImage: async () => {
      if (tableRef.current) {
        try {
          const dataUrl = await htmlToImage.toPng(tableRef.current);
          const link = document.createElement("a");
          link.download = "dashboard_table.png";
          link.href = dataUrl;
          link.click();
        } catch (error) {
          console.error("Error exporting image:", error);
        }
      }
    },
    copyData: async () => {
      try {
        const data = rows.map((row) => {
          const rowData = {};
          columns.forEach((col) => {
            rowData[col.headerName] = row[col.field];
          });
          return rowData;
        });
        await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
        alert("Data copied to clipboard!");
      } catch (err) {
        console.error("Error copying data:", err);
      }
    },
  }), [rows, columns]);

  const primaryHeaders = [
    "Skill Tower",
    "Total Demand",
    "Total Internal Supply",
    "Total External Supply",
    "Gap"
  ];
  const lighterHeaders = [
    "PDP",
    "VCDP",
    "NBL",
    "ROTATION",
    "Lateral Hiring CWR",
    "Lateral Hiring FTE"
  ];

  // PDP and VCDP column field names for grouping
  const pdpColumns = ["pdp", "pdp_(pa-)", "pdp_(a+)", "pdp_pa-", "pdp_a+"];
  const vcdpColumns = ["vcdp", "vcdp_(pa-)", "vcdp_(a+)", "vcdp_pa-", "vcdp_a+"];

  useMemo(() => {
    if (reportData && reportData.length > 0) {
      let dataKeys = Object.keys(reportData[0]);
      // remove any hideColumns
      dataKeys = dataKeys.filter(k => !hideColumns.includes(k));
      const firstColKey = dataKeys[0];
      
      // Separate Grand Total row from other rows
      const grandTotal = reportData.find(row => 
        String(row[firstColKey]).toLowerCase().includes('grand total')
      );
      const otherRows = reportData.filter(row => 
        !String(row[firstColKey]).toLowerCase().includes('grand total')
      );
      
      // Find the total_demand field (case insensitive)
      const totalDemandField = dataKeys.find(key => 
        key.toLowerCase().replace(/[_\s]/g, '') === 'totaldemand'
      );
      
      // Set default sort model if not already set
      if (sortModel.length === 0 && totalDemandField) {
        setSortModel([{ field: totalDemandField, sort: 'desc' }]);
      }
      
      // Find PDP and VCDP column fields and reorder: PA-, A+, then total
      const pdpFieldsRaw = dataKeys.filter(key => key.toLowerCase().startsWith("pdp"));
      // Sort: PA- first, A+ second, PDP total last
      const pdpFields = pdpFieldsRaw.sort((a, b) => {
        const aLower = a.toLowerCase();
        const bLower = b.toLowerCase();
        const aIsPaMinus = aLower.includes('pa-') || aLower.includes('(pa-)');
        const bIsPaMinus = bLower.includes('pa-') || bLower.includes('(pa-)');
        const aIsAPlus = aLower.includes('a+') || aLower.includes('(a+)');
        const bIsAPlus = bLower.includes('a+') || bLower.includes('(a+)');
        const aIsTotal = aLower === 'pdp';
        const bIsTotal = bLower === 'pdp';
        
        if (aIsPaMinus && !bIsPaMinus) return -1;
        if (!aIsPaMinus && bIsPaMinus) return 1;
        if (aIsAPlus && !bIsAPlus) return -1;
        if (!aIsAPlus && bIsAPlus) return 1;
        if (aIsTotal && !bIsTotal) return 1;
        if (!aIsTotal && bIsTotal) return -1;
        return 0;
      });
      
      const vcdpFieldsRaw = dataKeys.filter(key => key.toLowerCase().startsWith("vcdp"));
      // Sort: PA- first, A+ second, VCDP total last
      const vcdpFields = vcdpFieldsRaw.sort((a, b) => {
        const aLower = a.toLowerCase();
        const bLower = b.toLowerCase();
        const aIsPaMinus = aLower.includes('pa-') || aLower.includes('(pa-)');
        const bIsPaMinus = bLower.includes('pa-') || bLower.includes('(pa-)');
        const aIsAPlus = aLower.includes('a+') || aLower.includes('(a+)');
        const bIsAPlus = bLower.includes('a+') || bLower.includes('(a+)');
        const aIsTotal = aLower === 'vcdp';
        const bIsTotal = bLower === 'vcdp';
        
        if (aIsPaMinus && !bIsPaMinus) return -1;
        if (!aIsPaMinus && bIsPaMinus) return 1;
        if (aIsAPlus && !bIsAPlus) return -1;
        if (!aIsAPlus && bIsAPlus) return 1;
        if (aIsTotal && !bIsTotal) return 1;
        if (!aIsTotal && bIsTotal) return -1;
        return 0;
      });

      // Reorder dataKeys to put PDP and VCDP columns in correct order
      const reorderedKeys = dataKeys.filter(key => 
        !key.toLowerCase().startsWith('pdp') && !key.toLowerCase().startsWith('vcdp')
      );
      
      // Find where to insert PDP columns (after Total Internal Supply or at original position)
      const firstPdpIndex = dataKeys.findIndex(key => key.toLowerCase().startsWith('pdp'));
      const firstVcdpIndex = dataKeys.findIndex(key => key.toLowerCase().startsWith('vcdp'));
      
      // Insert sorted PDP and VCDP fields at their original positions
      if (firstPdpIndex !== -1) {
        reorderedKeys.splice(firstPdpIndex, 0, ...pdpFields);
      }
      if (firstVcdpIndex !== -1) {
        const adjustedVcdpIndex = firstVcdpIndex + (firstPdpIndex !== -1 ? pdpFields.length - pdpFieldsRaw.length : 0);
        reorderedKeys.splice(adjustedVcdpIndex, 0, ...vcdpFields);
      }

        const cols = reorderedKeys.map((key, index) => {
        const header = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
        let headerClassName = "";
        if (primaryHeaders.includes(header)) {
          headerClassName = "header-primary";
        } else if (lighterHeaders.includes(header)) {
          headerClassName = "header-lighter";
        }
        
        // Check if this is a numeric column (not the first column which is Skill Tower)
        const isFirstColumn = index === 0;
        const isPdpOrVcdp = key.toLowerCase().startsWith("pdp") || key.toLowerCase().startsWith("vcdp");
        const shouldCenterAlign = !isFirstColumn; // Center all columns except the first one
        const isNumericColumn = !isFirstColumn; // All columns except first are numeric
        
        return {
          field: key,
          headerName: header,
          minWidth: 150,
          flex: 1,
          type: isNumericColumn ? 'number' : 'string',
          align: shouldCenterAlign ? "center" : "left",
          headerAlign: shouldCenterAlign ? "center" : "left",
          renderCell: (params) =>
            index === 0 ? (
              <StyledClickableCell>{params.value}</StyledClickableCell>
            ) : (
              params.value
            ),
          cellClassName: (params) => getCellClassName(params, reportData, index),
          headerClassName,
        };
      });
      
      // Build column grouping model
      const groupingModel = [];
      
      if (pdpFields.length > 1) {
        groupingModel.push({
          groupId: "PDP",
          headerName: "PDP",
          headerClassName: "pdp-group-header",
          children: pdpFields.map(field => ({ field })),
        });
      }
      
      if (vcdpFields.length > 1) {
        groupingModel.push({
          groupId: "VCDP",
          headerName: "VCDP",
          headerClassName: "vcdp-group-header",
          children: vcdpFields.map(field => ({ field })),
        });
      }
      
      setColumnGroupingModel(groupingModel);
      setColumns(cols);
      
      // Store grand total separately and add it with a special id
      if (grandTotal) {
        setGrandTotalRow({ ...grandTotal, id: 'grand-total' });
      }
      
      // Set rows without grand total (will be appended in render)
        setRows(otherRows.map((row, index) => ({ ...row, id: index + 1 })));
    }
  }, [reportData]);

  const handleCellClick = async (params) => {
    if (params.field === columns[0].field) {
      const cellValue = params.value;
      setPopupTitle(`Demand Supply Dashboard - Skill : ${cellValue}`);
      setFilterContext({
        practice: filterValues.practice,
        market: filterValues.market,
        offOn: filterValues.offOn,
        skill: columns[0].headerName,
        value: cellValue,
      });
      try {
        const response = await axios.get(
          process.env.REACT_APP_API_BASE_URL+"/dashboard/detailview",
          {
            params: {
              practice: filterValues.practice,
              market: filterValues.market,
              offOn: filterValues.offOn,
              skill: cellValue,
            },
          }
        );
        const data = response.data;
        // Build popup column keys but remove PDP/VCDP PA- and A+ columns and the Skill Tower column
        const allKeys = Object.keys(data[0]);
        // Filter out explicit PA- and A+ columns for PDP/VCDP from the popup
        let keysFiltered = allKeys.filter(k => !/(pa-|\(pa-\)|a\+|\(a\+\))/i.test(k));

        // Identify grade and skill-tower-like keys
        const gradeKey = keysFiltered.find(k => /(^|_|\b)grade\b/i.test(k));
        const skillTowerKey = keysFiltered.find(k => /(skill.*tower|skill[_ ]?tower|^skill$)/i.test(k));

        // Remove skill-tower key from popup if present
        if (skillTowerKey) {
          keysFiltered = keysFiltered.filter(k => k !== skillTowerKey);
        }

        // Remove grade key from popup as requested
        if (gradeKey) {
          keysFiltered = keysFiltered.filter(k => k !== gradeKey);
        }

        const firstKey = allKeys[0];
        const pdpKeys = keysFiltered.filter(k => k.toLowerCase().startsWith('pdp'));
        const vcdpKeys = keysFiltered.filter(k => k.toLowerCase().startsWith('vcdp'));

        // Identify total demand key (if present) so we can place it right after grade
        const totalDemandKey = keysFiltered.find(k => /total[_\s]*demand/i.test(k));

        const matchOrder = (keys) => {
          const paMinus = keys.find(k => /pa-|\(pa-\)/i.test(k));
          const aPlus = keys.find(k => /a\+|\(a\+\)/i.test(k));
          const rest = keys.filter(k => k !== paMinus && k !== aPlus);
          return [paMinus, aPlus, ...rest].filter(Boolean);
        };

        const orderedPdp = matchOrder(pdpKeys);
        const orderedVcdp = matchOrder(vcdpKeys);

        // Build final key order: put `grade` first (if present), then PDP/VCDP remaining keys, then other keys
        const keyToUseAsFirst = gradeKey || firstKey;
        // Remove the chosen first key from the pool so it won't be duplicated
        let remainingKeys = keysFiltered.filter(k => k !== keyToUseAsFirst);
        // If we have a total demand key, remove it from remaining and insert after grade
        if (totalDemandKey) {
          remainingKeys = remainingKeys.filter(k => k !== totalDemandKey);
        }
        const otherKeys = remainingKeys.filter(k => !pdpKeys.includes(k) && !vcdpKeys.includes(k));
        const finalKeyOrder = [
          keyToUseAsFirst,
          ...(totalDemandKey && totalDemandKey !== keyToUseAsFirst ? [totalDemandKey] : []),
          ...orderedPdp,
          ...orderedVcdp,
          ...otherKeys,
        ];

        const cols = finalKeyOrder.map((key, index) => {
          const header = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
          let headerClassName = "";
          if (primaryHeaders.includes(header)) {
            headerClassName = "header-primary";
          } else if (lighterHeaders.includes(header)) {
            headerClassName = "header-lighter";
          }
          const isFirstColumn = index === 0;
          const shouldCenterAlign = !isFirstColumn;
          return {
            field: key,
            headerName: header,
            flex: 1,
            align: shouldCenterAlign ? "center" : "left",
            headerAlign: shouldCenterAlign ? "center" : "left",
            // Pass isPopup=true to avoid middle-column 'high-pdp' coloring
            cellClassName: (params) => getCellClassName(params, data, index, true),
            headerClassName,
          };
        });
        setPopupData({
          rows: data.map((row, index) => ({ ...row, id: index + 1 })),
          columns: cols,
        });
        setOpenPopup(true);
      } catch (error) {
        console.error("Error fetching popup data:", error);
      }
    }
  };

  const getCellClassName = (params, reportData, index, isPopup = false) => {
    const isFirstColumn = index === 0;

    // Determine last-column reliably using the grid API when available (works for popup grids)
    let isLastColumn = false;
    try {
      if (params && params.api && typeof params.api.getAllColumns === "function") {
        const allCols = params.api.getAllColumns();
        const lastField = allCols && allCols.length ? allCols[allCols.length - 1].field : null;
        isLastColumn = params.colDef && params.colDef.field === lastField;
      }
    } catch (e) {
      // fallback to original logic
      isLastColumn = index === Object.keys(reportData[0] || {}).length - 1;
    }

    // Determine last-row (best-effort): if api provides rowCount use it, else fallback
    let isLastRow = false;
    try {
      if (params && params.api && typeof params.api.getRows === "function") {
        // Some DataGrid APIs expose getRows; try to infer via rowCount if available
        const rowCount = params.api.getRowCount ? params.api.getRowCount() : null;
        if (rowCount !== null && rowCount !== undefined) {
          isLastRow = params.row && params.row.id === rowCount;
        } else {
          isLastRow = params.row && params.row.id === (reportData.length);
        }
      } else {
        isLastRow = params.row && params.row.id === (reportData.length);
      }
    } catch (e) {
      isLastRow = params.row && params.row.id === (reportData.length);
    }

    const numericValue = Number(params.value);
    const isGapNegative = isLastColumn && !isNaN(numericValue) && numericValue < 0;
    const isGapPostive = isLastColumn && !isNaN(numericValue) && numericValue >= 0;

    // Return combined class names (no high-pdp styling here)
    return `${isLastRow ? "last-row" : ""} ${isFirstColumn ? "first-column" : ""} ${isLastColumn ? "last-column" : ""} ${isGapPostive ? "positive-value" : ""} ${isGapNegative ? "negative-value" : ""}`.trim();
  };

  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Dashboard Data");
    XLSX.writeFile(workbook, "dashboard_data.xlsx");
  };

  const handleExportImage = async () => {
    if (tableRef.current) {
      try {
        const dataUrl = await htmlToImage.toPng(tableRef.current);
        const link = document.createElement("a");
        link.download = "dashboard_table.png";
        link.href = dataUrl;
        link.click();
      } catch (error) {
        console.error("Error exporting image:", error);
      }
    }
  };

  const handleCopyData = async () => {
    try {
      const data = rows.map((row) => {
        const rowData = {};
        columns.forEach((col) => {
          rowData[col.headerName] = row[col.field];
        });
        return rowData;
      });
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      alert("Data copied to clipboard!");
    } catch (err) {
      console.error("Error copying data:", err);
    }
  };

  const handleExportPopupExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(popupData.rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Popup Data");
    XLSX.writeFile(workbook, "popup_data.xlsx");
  };

  const handleExportPopupImage = async () => {
    if (popupTableRef.current) {
      try {
        const dataUrl = await htmlToImage.toPng(popupTableRef.current);
        const link = document.createElement("a");
        link.download = "popup_table.png";
        link.href = dataUrl;
        link.click();
      } catch (error) {
        console.error("Error exporting popup image:", error);
      }
    }
  };

  const handleCopyPopupData = async () => {
    try {
      await navigator.clipboard.writeText(
        JSON.stringify(popupData.rows, null, 2)
      );
      alert("Popup data copied to clipboard!");
    } catch (error) {
      console.error("Error copying popup data:", error);
    }
  };

  // Custom sort to always keep Grand Total at the end
  const displayRows = useMemo(() => {
    if (!grandTotalRow) return rows;
    
    // If there's a sort model, apply sorting manually to keep grand total at end
    let sortedRows = [...rows];
    
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      sortedRows.sort((a, b) => {
        const valA = Number(a[field]) || 0;
        const valB = Number(b[field]) || 0;
        if (sort === 'asc') {
          return valA - valB;
        } else {
          return valB - valA;
        }
      });
    }
    
    // Always append grand total at the end
    return [...sortedRows, grandTotalRow];
  }, [rows, grandTotalRow, sortModel]);

  return (
    <Box sx={{ position: "relative", height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        ref={tableRef}
        sx={{ flex: 1, minHeight: 0, width: "100%" }}
      >
        <StyledDataGrid
          rows={displayRows}
          columns={columns.map((col) => ({
            ...col,
            flex: 1, // Adjust column size dynamically to fit the table width
            minWidth: 100, // Set a minimum width for columns
          }))}
          columnGroupingModel={columnGroupingModel}
          experimentalFeatures={{ columnGrouping: true }}
          rowHeight={25}
          columnHeaderHeight={56}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          pagination={false}
          hideFooterPagination
          autoHeight={false}
          onCellClick={handleCellClick}
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model)}
          sortingMode={sortingMode}
          sortingOrder={['desc', 'asc', null]}
          sx={{
            "& .MuiDataGrid-main": { overflow: "auto" },
            "& .MuiDataGrid-virtualScroller": { overflow: "auto !important" },
          }}
        />
      </Box>

      <Dialog
  open={openPopup}
  onClose={() => setOpenPopup(false)}
  maxWidth="x1"
  fullWidth
  >
  <DialogTitle
    sx={{
      display: "flex",
      alignItems: "center",
      backgroundColor: primaryColor,
      color: "#fff",
      padding: "16px",
      fontWeight: "bold",
      
    }}
  >
    <Typography variant="h6" component="div">
      {popupTitle}
    </Typography>
    <IconButton
      onClick={() => setOpenPopup(false)}
      sx={{ marginLeft: "auto", color: "#fff" }}
    >
      <CloseIcon />
    </IconButton>
  </DialogTitle>
  <DialogContent>
          {filterContext && (
            <Box sx={{ mb: 2, p: 1, bgcolor: lightGrey, borderRadius: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                  <Typography component="span" sx={{ fontWeight: "bold" }}>
                    Practice:
                  </Typography>
                  <Typography component="span">
                    {filterContext.practice}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                  <Typography component="span" sx={{ fontWeight: "bold" }}>
                    Market:
                  </Typography>
                  <Typography component="span">
                    {filterContext.market}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                  <Typography component="span" sx={{ fontWeight: "bold" }}>
                    Off/On:
                  </Typography>
                  <Typography component="span">
                    {filterContext.offOn}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                  <Typography component="span" sx={{ fontWeight: "bold" }}>
                    Skill:
                  </Typography>
                  <Typography component="span">
                    {filterContext.value}
                  </Typography>
                </Box>
              </Typography>
              <Divider sx={{ borderColor: darkGrey }} />
            </Box>
          )}
          <Box sx={{ position: "relative", paddingTop: 5 }}>
            <Box
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                zIndex: 1,
                display: "flex",
                gap: 1,
                padding: 1,
              }}
            >
              <IconButton
                onClick={handleExportPopupExcel}
                title="Export Excel"
                sx={{ color: primaryColor }}
              >
                <FileDownload />
              </IconButton>
              <IconButton
                onClick={handleExportPopupImage}
                title="Export Image"
                sx={{ color: primaryColor }}
              >
                <Image />
              </IconButton>
              <IconButton
                onClick={handleCopyPopupData}
                title="Copy Data"
                sx={{ color: primaryColor }}
              >
                <ContentCopy />
              </IconButton>
            </Box>

            <Box
              ref={popupTableRef}
              id="popup-table"
              style={{ height: 400, width: "100%", overflow: "auto" }}
            >
              <StyledDataGrid
                rows={popupData.rows}
                columns={popupData.columns}
                autoHeight
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                disableSelectionOnClick
                pagination={false}
                hideFooterPagination
                sx={{
                  "& .MuiDataGrid-main": { overflow: "auto" },
                  "& .MuiDataGrid-virtualScroller": {
                    overflow: "auto !important",
                  },
                }}
              />
            </Box>
          </Box>
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button onClick={() => setOpenPopup(false)} variant="outlined">
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
});

export default DashboardTable;